import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Download, ChevronLeft, ChevronRight,
  Loader2, ClipboardList, MapPin, CheckCircle,
  AlertTriangle, XCircle, RefreshCw, Cloud
} from 'lucide-react';
import API from '../api/axios';

const fetchSubmissions = (params) => API.get('/submissions', { params }).then(r => r.data);
const fetchUsers = () => API.get('/users').then(r => r.data);
const fetchActivities = () => API.get('/activity-types').then(r => r.data);
const fetchTerritories = () => API.get('/territories').then(r => r.data);
const fetchShops = () => API.get('/shops').then(r => r.data);

const SYNC_STATUS = {
  synced:    { label: 'Synced',    bg: 'bg-teal-50 text-teal-700 border border-teal-200',   dot: 'bg-teal-500' },
  pending:   { label: 'Pending',   bg: 'bg-amber-50 text-amber-700 border border-amber-200', dot: 'bg-amber-400' },
  failed:    { label: 'Failed',    bg: 'bg-red-50 text-red-600 border border-red-200',       dot: 'bg-red-500' },
  uploading: { label: 'Uploading', bg: 'bg-blue-50 text-blue-600 border border-blue-200',    dot: 'bg-blue-500' },
  draft:     { label: 'Draft',     bg: 'bg-gray-100 text-gray-500 border border-gray-200',   dot: 'bg-gray-400' },
};

const LOCATION_STATUS = {
  valid:                  { label: 'Verified',      icon: <CheckCircle size={13} className="text-teal-500" /> },
  warning_outside_radius: { label: 'GPS Mismatch',  icon: <AlertTriangle size={13} className="text-amber-500" /> },
  invalid_no_location:    { label: 'No Location',   icon: <XCircle size={13} className="text-red-500" /> },
  invalid_poor_accuracy:  { label: 'Poor Accuracy', icon: <AlertTriangle size={13} className="text-red-500" /> },
  not_required:           { label: 'Not Required',  icon: <MapPin size={13} className="text-gray-400" /> },
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const formatTime = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};
const getInitials = (name = '') => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
const avatarColors = ['bg-teal-600','bg-violet-500','bg-blue-500','bg-emerald-600','bg-rose-500','bg-amber-500'];
const getAvatarColor = (name = '') => avatarColors[name.charCodeAt(0) % avatarColors.length];

const Submissions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const [dateRange, setDateRange] = useState('All Time');
  const [employeeFilter, setEmployeeFilter] = useState(searchParams.get('user') || '');
  const [activityFilter, setActivityFilter] = useState('');
  const [territoryFilter, setTerritoryFilter] = useState('');
  const [shopFilter, setShopFilter] = useState(searchParams.get('shop') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');

  const getDateFrom = () => {
    if (dateRange === 'Today') { const d = new Date(); d.setHours(0,0,0,0); return d.toISOString(); }
    if (dateRange === 'Last 7 Days') return new Date(Date.now() - 7*24*60*60*1000).toISOString();
    if (dateRange === 'Last 30 Days') return new Date(Date.now() - 30*24*60*60*1000).toISOString();
    if (dateRange === 'Last 90 Days') return new Date(Date.now() - 90*24*60*60*1000).toISOString();
    return undefined;
  };

  const queryParams = {
    page, limit: 10,
    ...(employeeFilter && { user: employeeFilter }),
    ...(activityFilter && { activityType: activityFilter }),
    ...(shopFilter && { shop: shopFilter }),
    ...(statusFilter && { syncStatus: statusFilter }),
    ...(getDateFrom() && { dateFrom: getDateFrom() }),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['submissions', queryParams],
    queryFn: () => fetchSubmissions(queryParams),
    keepPreviousData: true,
  });

  const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const { data: activities = [] } = useQuery({ queryKey: ['activity-types'], queryFn: fetchActivities });
  const { data: territories = [] } = useQuery({ queryKey: ['territories-list'], queryFn: fetchTerritories });
  const { data: shops = [] } = useQuery({ queryKey: ['shops'], queryFn: fetchShops });

  const submissions = data?.submissions || [];
  const total = data?.total || 0;
  const totalPages = data?.pages || 1;

  const syncRate = submissions.length > 0
    ? Math.round((submissions.filter(s => s.syncStatus === 'synced').length / submissions.length) * 100)
    : 0;
  const failedCount = submissions.filter(s => s.syncStatus === 'failed').length;

  const exportCSV = () => {
    const rows = [['Date', 'Time', 'Employee', 'Activity', 'Shop', 'Location Status', 'Sync Status']];
    submissions.forEach(s => rows.push([
      formatDate(s.createdAt), formatTime(s.createdAt),
      s.user?.name || '', s.activityType?.name || '',
      s.shop?.name || '', s.location?.validationStatus || '', s.syncStatus || '',
    ]));
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv,' + encodeURIComponent(csv);
    a.download = 'submissions.csv'; a.click();
  };

  const FilterSelect = ({ label, value, onChange, children }) => (
    <div>
      <label className="text-xs font-medium text-gray-400 block mb-1">{label}</label>
      <select value={value} onChange={e => { onChange(e.target.value); setPage(1); }}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 text-gray-700">
        {children}
      </select>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 min-h-full">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Submissions</h1>
          <p className="text-sm text-gray-500 mt-0.5">Review and manage field operational data entries.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowFilters(v => !v)}
            className="sm:hidden flex items-center gap-2 px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">
            <RefreshCw size={14} /> Filters
          </button>
          <button onClick={exportCSV}
            className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-medium transition-colors">
            <Download size={15} />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={`bg-white rounded-xl border border-gray-200 p-4 ${showFilters ? 'block' : 'hidden sm:block'}`}>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          <FilterSelect label="Date Range" value={dateRange} onChange={setDateRange}>
            <option>All Time</option>
            <option>Today</option>
            <option>Last 7 Days</option>
            <option>Last 30 Days</option>
            <option>Last 90 Days</option>
          </FilterSelect>
          <FilterSelect label="Employee" value={employeeFilter} onChange={setEmployeeFilter}>
            <option value="">All Employees</option>
            {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </FilterSelect>
          <FilterSelect label="Activity Type" value={activityFilter} onChange={setActivityFilter}>
            <option value="">All Activities</option>
            {activities.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
          </FilterSelect>
          <FilterSelect label="Territory" value={territoryFilter} onChange={setTerritoryFilter}>
            <option value="">All Territories</option>
            {territories.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
          </FilterSelect>
          <FilterSelect label="Shop" value={shopFilter} onChange={setShopFilter}>
            <option value="">All Shops</option>
            {shops.slice(0,50).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </FilterSelect>
          <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter}>
            <option value="">All Statuses</option>
            <option value="synced">Synced</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="uploading">Uploading</option>
            <option value="draft">Draft</option>
          </FilterSelect>
        </div>
      </div>

      {/* ── DESKTOP TABLE ── */}
      <div className="hidden sm:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-[1.5fr_1.5fr_1.5fr_1.5fr_1.2fr_1.3fr_1.3fr] gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <div>Submitted At</div><div>Employee</div><div>Activity</div>
          <div>Shop</div><div>Territory</div><div>Location</div><div>Status</div>
        </div>

        {isLoading && <div className="flex items-center justify-center py-16"><Loader2 size={24} className="text-gray-300 animate-spin" /></div>}

        {!isLoading && submissions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <ClipboardList size={36} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">No submissions found</p>
            <p className="text-xs mt-1">Try adjusting your filters</p>
          </div>
        )}

        {!isLoading && submissions.map((s, i) => {
          const sync = SYNC_STATUS[s.syncStatus] || SYNC_STATUS.draft;
          const loc = LOCATION_STATUS[s.location?.validationStatus] || LOCATION_STATUS.not_required;
          return (
            <div key={s._id} onClick={() => navigate(`/submissions/${s._id}`)}
              className={`grid grid-cols-[1.5fr_1.5fr_1.5fr_1.5fr_1.2fr_1.3fr_1.3fr] gap-3 px-5 py-3.5 border-b border-gray-100 hover:bg-gray-50 transition-colors items-center cursor-pointer ${i === submissions.length - 1 ? 'border-b-0' : ''}`}>
              <div>
                <div className="text-sm font-medium text-gray-800">{formatDate(s.createdAt)}</div>
                <div className="text-xs text-gray-400">{formatTime(s.createdAt)}</div>
              </div>
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={`w-7 h-7 rounded-full ${getAvatarColor(s.user?.name || '')} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                  {getInitials(s.user?.name || 'U')}
                </div>
                <span className="text-sm text-gray-700 truncate">{s.user?.name || '—'}</span>
              </div>
              <div>
                {s.activityType?.name
                  ? <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">{s.activityType.name}</span>
                  : <span className="text-gray-400 text-sm">—</span>}
              </div>
              <div className="min-w-0">
                <div className="text-sm text-gray-700 truncate">{s.shop?.name || '—'}</div>
                {s.shop?.area && <div className="text-xs text-gray-400 truncate">{s.shop.area}</div>}
              </div>
              <div className="text-sm text-gray-600">{s.shop?.city || '—'}</div>
              <div className="flex items-center gap-1.5">
                {loc.icon}
                <span className="text-xs text-gray-600">{loc.label}</span>
              </div>
              <div>
                <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${sync.bg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${sync.dot}`} />
                  {sync.label}
                </span>
              </div>
            </div>
          );
        })}

        {/* Desktop Pagination */}
        {!isLoading && total > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <span className="text-sm text-gray-500">Showing 1–{Math.min(page * 10, total)} of {total}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 text-gray-600">Previous</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pg = i + 1;
                if (totalPages > 5 && page > 3) pg = page - 2 + i;
                if (pg > totalPages) return null;
                return <button key={pg} onClick={() => setPage(pg)} className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${page === pg ? 'bg-slate-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{pg}</button>;
              })}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 text-gray-600">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* ── MOBILE CARDS ── */}
      <div className="sm:hidden space-y-3">
        {isLoading && <div className="flex items-center justify-center py-16"><Loader2 size={24} className="text-gray-300 animate-spin" /></div>}

        {!isLoading && submissions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200">
            <ClipboardList size={36} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">No submissions found</p>
          </div>
        )}

        {!isLoading && submissions.map(s => {
          const sync = SYNC_STATUS[s.syncStatus] || SYNC_STATUS.draft;
          const loc = LOCATION_STATUS[s.location?.validationStatus] || LOCATION_STATUS.not_required;
          return (
            <div key={s._id} onClick={() => navigate(`/submissions/${s._id}`)}
              className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer active:bg-gray-50">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`w-9 h-9 rounded-full ${getAvatarColor(s.user?.name || '')} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                    {getInitials(s.user?.name || 'U')}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{s.user?.name || '—'}</div>
                    <div className="text-xs text-gray-400">{formatDate(s.createdAt)} · {formatTime(s.createdAt)}</div>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${sync.bg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${sync.dot}`} />
                  {sync.label}
                </span>
              </div>
              <div className="space-y-1.5">
                {s.activityType?.name && (
                  <span className="inline-block text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{s.activityType.name}</span>
                )}
                {s.shop?.name && (
                  <div className="text-xs text-gray-500 truncate">📍 {s.shop.name}{s.shop.area ? `, ${s.shop.area}` : ''}</div>
                )}
                <div className="flex items-center gap-1.5">
                  {loc.icon}
                  <span className="text-xs text-gray-500">{loc.label}</span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Mobile Pagination */}
        {!isLoading && total > 0 && (
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-gray-500">1–{Math.min(page * 10, total)} of {total}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg border border-gray-200 disabled:opacity-30 text-gray-500 bg-white"><ChevronLeft size={15} /></button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg border border-gray-200 disabled:opacity-30 text-gray-500 bg-white"><ChevronRight size={15} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Sync Rate', value: `${syncRate}%`, icon: <Cloud size={18} className="text-teal-500" />, bg: 'bg-teal-50' },
          { label: 'Queue Depth', value: `${submissions.filter(s => s.syncStatus === 'pending').length}`, icon: <ClipboardList size={18} className="text-amber-500" />, bg: 'bg-amber-50' },
          { label: 'Avg. Upload', value: '1.4s', icon: <RefreshCw size={18} className="text-blue-500" />, bg: 'bg-blue-50' },
          { label: 'Failed Rate', value: `${failedCount > 0 ? ((failedCount / Math.max(submissions.length, 1)) * 100).toFixed(1) : 0}%`, icon: <AlertTriangle size={18} className="text-red-500" />, bg: 'bg-red-50' },
        ].map((c, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 flex items-center gap-3">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl ${c.bg} flex items-center justify-center flex-shrink-0`}>{c.icon}</div>
            <div>
              <div className="text-xs text-gray-400 font-medium">{c.label}</div>
              <div className="text-base sm:text-lg font-bold text-gray-900">{c.value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Submissions;